import {Alert, Radio, RadioGroup} from '@mui/material'
import React, {useMemo, useState} from 'react'
import {Box} from '../../../webui/Box'
import {Button} from '../../../webui/Button'
import {Dialog} from '../../../webui/Dialog'
import {DialogActions} from '../../../webui/DialogActions'
import {DialogContent} from '../../../webui/DialogContent'
import {DialogContentText} from '../../../webui/DialogContentText'
import {DialogTitle} from '../../../webui/DialogTitle'
import {FormControlLabel} from '../../../webui/inputs/FormControlLabel'
import {Option, SearchSelect} from '../../../webui/inputs/SearchSelect'
import {TextField} from '../../../webui/inputs/TextField'
import {Typography} from '../../../webui/Typography'

// A box that already holds a serial number — the set of boxes a serial can be moved from.
export interface SerialOwner {
  id: string
  name: string
  serialNumber: string
}

export interface SerialPromptResult {
  // Empty string means "no serial — assign one later".
  serialNumber: string
  // Set when the serial is being moved off an existing box (reusing a physical box).
  transferFromBoxId: string
  // Name of that box, for confirmation/linking after the move.
  transferFromBoxName: string
}

interface Props {
  open: boolean
  // All boxes that currently have a serial number (from boxesForSerialAdmin). These are the
  // boxes a serial can be moved from.
  existingSerials: SerialOwner[]
  onCancel: () => void
  onConfirm: (result: SerialPromptResult) => void
}

type Mode = 'none' | 'enter' | 'move'

const normalize = (serial: string): string => serial.trim().toUpperCase()

const MODE_OPTIONS: {value: Mode; title: string; description: string}[] = [
  {
    value: 'none',
    title: 'No — assign a serial number later',
    description: 'A serial number can be set later. (For now, ask a developer to assign one.)',
  },
  {
    value: 'enter',
    title: 'Enter a serial number',
    description: "For a serial that isn't in the system yet — e.g. a new box marked with its own sticker.",
  },
  {
    value: 'move',
    title: 'Move a physical box from another organisation',
    description: 'Reuse a physical box that already exists — its serial number will be moved to this (virtual) box when you save.',
  },
]

export const SerialNumberPrompt: React.FC<Props> = ({open, existingSerials, onCancel, onConfirm}) => {
  const [mode, setMode] = useState<Mode>('none')
  const [serialInput, setSerialInput] = useState('')
  const [moveBoxId, setMoveBoxId] = useState('')

  const normalized = normalize(serialInput)

  const moveOptions: Option[] = useMemo(
    () => existingSerials.map((box) => ({value: box.id, label: `${box.name} · ${box.serialNumber}`})),
    [existingSerials],
  )
  const selectedMoveOption = moveOptions.find((option) => option.value === moveBoxId) ?? null

  // In "enter" mode, warn if the typed serial already belongs to a box — the user should use
  // the "move" path instead of creating a guaranteed collision.
  const enterCollision = useMemo(() => {
    if (mode !== 'enter' || normalized === '') return undefined
    return existingSerials.find((box) => normalize(box.serialNumber) === normalized)
  }, [mode, normalized, existingSerials])

  const canContinue = mode === 'none' || (mode === 'enter' && normalized !== '' && !enterCollision) || (mode === 'move' && moveBoxId !== '')

  const handleConfirm = () => {
    if (mode === 'move') {
      const box = existingSerials.find((b) => b.id === moveBoxId)
      onConfirm({
        serialNumber: box ? normalize(box.serialNumber) : '',
        transferFromBoxId: box?.id ?? '',
        transferFromBoxName: box?.name ?? '',
      })
      return
    }
    if (mode === 'enter') {
      onConfirm({serialNumber: normalized, transferFromBoxId: '', transferFromBoxName: ''})
      return
    }
    onConfirm({serialNumber: '', transferFromBoxId: '', transferFromBoxName: ''})
  }

  const changeMode = (next: Mode) => {
    setMode(next)
  }

  return (
    <Dialog open={open} onClose={onCancel} aria-labelledby="serial-prompt-title">
      <DialogTitle id="serial-prompt-title">Does this box have a serial number?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          The serial number identifies the physical metal box (marked with a sticker), and each serial belongs to one box only.
        </DialogContentText>

        <Box mt={2}>
          <RadioGroup value={mode} onChange={(_, value) => changeMode(value as Mode)}>
            {MODE_OPTIONS.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio color="primary" />}
                sx={{alignItems: 'flex-start', mb: 1, '& .MuiFormControlLabel-label': {mt: 0.5}}}
                label={
                  <Box>
                    <Typography variant="body1">{option.title}</Typography>
                    <Typography variant="caption" color="text.secondary" component="div">
                      {option.description}
                    </Typography>
                  </Box>
                }
              />
            ))}
          </RadioGroup>
        </Box>

        {mode === 'enter' && (
          <Box mt={1}>
            <TextField id="serial-prompt-input" label="Serial number" value={serialInput} onChange={(value) => setSerialInput(value)} />
            {enterCollision && (
              <Box mt={2}>
                <Alert severity="warning">
                  <Typography variant="body2">
                    This serial number is already assigned to <strong>{enterCollision.name}</strong>. To reuse that physical box here, choose{' '}
                    <strong>Move a physical box from another organisation</strong> above instead.
                  </Typography>
                </Alert>
              </Box>
            )}
          </Box>
        )}

        {mode === 'move' && (
          // Reserve vertical space so the dropdown's menu opens within the dialog instead of
          // being clipped by the scrollable content area (which forced the user to scroll).
          <Box mt={1} minHeight={moveOptions.length === 0 ? undefined : 320}>
            {moveOptions.length === 0 ? (
              <Alert severity="info">No boxes currently have a serial number, so there's nothing to move.</Alert>
            ) : (
              <>
                <SearchSelect
                  label="Move the serial from"
                  placeholder="Search by box name or serial…"
                  options={moveOptions}
                  value={selectedMoveOption}
                  onChange={(option) => setMoveBoxId(option?.value ?? '')}
                />
                {selectedMoveOption && (
                  <Box mt={2}>
                    <Alert severity="info">
                      <Typography variant="body2">
                        The serial number will be moved from <strong>{selectedMoveOption.label}</strong> to this new box when you save.
                      </Typography>
                    </Alert>
                  </Box>
                )}
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="primary">
          Cancel
        </Button>
        <Button onClick={handleConfirm} color="primary" disabled={!canContinue} autoFocus>
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  )
}
